import {Box} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';
import {match} from 'ts-pattern';

import {useCallback, useState} from 'react';
import {useDispatch} from 'react-redux';

import {head, isNil, mergeAll} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {metadaApi} from '@dms/api/base';
import {TotalPriceWithAndWithoutVat, useDeleteSupplierOrderItemsMutation} from '@dms/api/warehouse';
import {testIds} from '@dms/routes';
import {
  DmsDataGrid,
  WAREHOUSE_GRID_CODES,
  catchUnhandledDataGridActions,
  handleApiError,
  logError,
  useTenant,
} from '@dms/shared';
import {OverviewTotalPrice, useWarehouseParams} from '@dms/warehouse/shared-components';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

import {SupplierOrderItemEdit, SupplierOrderItemEditProps} from './SupplierOrderItemEdit';

interface ListOfItemsTabProps extends TestIdProps {
  isReadOnly: boolean;
  totalPrice: TotalPriceWithAndWithoutVat | Nullish;
  isPending: boolean;
}

export function ListOfItemsTab(props: ListOfItemsTabProps) {
  const {supplierOrderId} = useWarehouseParams();
  const formatCurrency = useFormatCurrency();

  const dispatch = useDispatch();

  const tenant = useTenant();

  const [editDialogProps, setEditDialogProps] = useState<SupplierOrderItemEditProps | null>(null);

  const [deleteSupplierOrderItems] = useDeleteSupplierOrderItemsMutation();

  const totalPriceWithoutVat = formatCurrency(
    props.totalPrice?.totalWithoutVat?.amount ?? 0,
    props.totalPrice?.totalWithoutVat?.currency ?? tenant.tenantCurrency,
    2
  );

  const totalPriceWithVat = formatCurrency(
    props.totalPrice?.totalWithVat?.amount ?? 0,
    props.totalPrice?.totalWithVat?.currency ?? tenant.tenantCurrency,
    2
  );

  const actionCallback: ActionCallback = async ({actionKey, rowId, queryId, refreshData}) => {
    await match(actionKey)
      .with('redirectDetail', 'edit', () => {
        const supplierOrderItemId = isArray(rowId) ? head(rowId) : rowId;

        if (isNil(supplierOrderItemId)) {
          return logError('Item id is not defined!');
        }

        const handleClose = () => {
          setEditDialogProps(null);
          refreshData();
          // Invalidating manually onClose to keep dialog rowControl saving feature intact.
          dispatch(metadaApi.util.invalidateTags(['supplierOrders']));
        };

        setEditDialogProps({
          supplierOrderId,
          supplierOrderItemId,
          dataQueryId: queryId,
          isReadOnly: props.isReadOnly,
          onClose: handleClose,
          'data-testid': testIds.warehouse.supplierOrderDetailOverview('editItem'),
        });
      })
      .with('delete', async () => {
        const itemId = isArray(rowId) ? rowId : [rowId];

        await deleteSupplierOrderItems({orderId: supplierOrderId, body: {itemId}})
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError);
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => mergeAll([filter, {orderId: supplierOrderId}]),
    [supplierOrderId]
  );

  return (
    <>
      {isNil(editDialogProps) ? null : <SupplierOrderItemEdit {...editDialogProps} />}
      <Box height="95%">
        <DmsDataGrid
          gridCode={WAREHOUSE_GRID_CODES.warehouseSupplierOrderItems}
          actionCallback={actionCallback}
          queryModifier={queryModifier}
          data-testid={WAREHOUSE_GRID_CODES.warehouseSupplierOrderItems}
        />
      </Box>
      <OverviewTotalPrice
        totalPriceWithoutVat={totalPriceWithoutVat}
        totalPriceWithVat={totalPriceWithVat}
        data-testid={suffixTestId('totalPrice', props)}
      />
    </>
  );
}
